import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router'
import { useBeforeUnload } from 'react-use'

import { Suspense } from '~/components/elements/Suspense'
import { checkCurrentSurvey } from '~/hoc/checkCurrentSurvey'
import { useLogEventOnMount } from '~/hooks/useLogEventOnMount'
import { type Brand, useSurveyQuery } from '~/hooks/useSurveyQuery'

import {
  brandIdsAtom,
  fiveSegmentCommentAtom,
  nineSegmentCommentAtom,
  savedFiveSegmentCommentAtom,
  savedNineSegmentCommentAtom,
} from './atoms'
import { StandardAnalysis } from './components/StandardAnalysis'
import { useResetOnUnmount } from './hooks/useResetOnUnmount'

// 表示中（選択中）のブランドのコメントだけで未保存判定する。
// ゴミ箱で削除したブランドの残存コメントは無視する。
const commentsDifferForBrands = (
  brandIds: Brand['id'][],
  comment: Partial<Record<string, string>>,
  saved: Partial<Record<string, string>>,
): boolean => brandIds.some((id) => (comment[id] ?? '') !== (saved[id] ?? ''))

export const StandardAnalysisPage = checkCurrentSurvey(() => {
  const { t } = useTranslation()
  const { isLoading } = useSurveyQuery()
  const brandIds = useAtomValue(brandIdsAtom)
  const fiveSegmentComment = useAtomValue(fiveSegmentCommentAtom)
  const nineSegmentComment = useAtomValue(nineSegmentCommentAtom)
  const savedFiveSegmentComment = useAtomValue(savedFiveSegmentCommentAtom)
  const savedNineSegmentComment = useAtomValue(savedNineSegmentCommentAtom)
  const isDirty =
    commentsDifferForBrands(
      brandIds,
      fiveSegmentComment,
      savedFiveSegmentComment,
    ) ||
    commentsDifferForBrands(
      brandIds,
      nineSegmentComment,
      savedNineSegmentComment,
    )
  const dirtyMessage = t(
    'edited_content_has_not_been_saved_do_you_still_want_to_leave',
  )

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  )

  useBeforeUnload(isDirty, dirtyMessage)
  useLogEventOnMount('Access to 9segs customer data function')
  useResetOnUnmount()

  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      const isConfirmed = window.confirm(dirtyMessage)
      if (isConfirmed) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker, dirtyMessage])

  return (
    <Suspense loading={isLoading}>
      <StandardAnalysis />
    </Suspense>
  )
})
